import React from 'react'
import { Field, useFormikContext } from 'formik'
import { Message } from 'react-bulma-components'
import ListField from '../../../../form/fields/list-field'
import { AutocompleteFieldItem, ControlledAutocomplete } from '../../../../form/fields/autocomplete'
import { buildListAsInputOptions } from '../../../../../utils/forms'
import { useWorkersQuery } from '../../../../../queries/users'
import { User } from '../../../../../api/users'
import { Employer } from '../../../../../api/organizations'
import WorkerLabel from '../../../../texts/worker-label'
import { getUserLabel, workerIsEmployerCollaborationInactive } from '../../../../../utils/users'
import WorkerInputLabel from '../../worker-input-label'
import { WorkersRequestCreateUpdateFormValues } from '../workers-request-form.types'

/**
 * Props of the `DesiredWorkersField` component.
 */
interface DesiredWorkersFieldProps {
  fieldName: string
  fieldValue: WorkersRequestCreateUpdateFormValues['posts'][number]['desiredWorkers']
  employer: Employer
}

/**
 * Field that lets the employer pick a list of "desired workers" for a post.
 *
 * Internally:
 * - The selected list is rendered by `<ListField>` (Formik-wired).
 * - The "pick a new worker" autocomplete is implemented with `ControlledAutocomplete`
 *   + a local `useState` for the temporary input value. When the user picks a worker
 *   the id is pushed into the Formik array via `setFieldValue` from the parent
 *   Formik context. No nested `<Formik>` is needed.
 */
const DesiredWorkersField: React.FC<DesiredWorkersFieldProps> = ({
  fieldName,
  fieldValue,
  employer,
}) => {
  const {
    values: {
      interimAgency,
      meta: { resource: workersRequest },
    },
    setFieldValue,
  } = useFormikContext<WorkersRequestCreateUpdateFormValues>()

  // Workers list is only queried once an interim agency is selected.
  const workersQuery = useWorkersQuery({ interimAgency }, { enabled: Boolean(interimAgency) })

  const allWorkers = (workersQuery.data || []).filter(
    w => !workerIsEmployerCollaborationInactive(w, employer),
  )

  // Already-selected workers shown in the `<ListField>` above the autocomplete.
  const selectedWorkers = fieldValue
    .map(dWId => allWorkers?.find(w => w._id === dWId))
    .filter((w): w is User => !!w)

  const selectedItems = buildListAsInputOptions(selectedWorkers ?? [], {
    labelBuilder: user => getUserLabel(user),
    renderLabel: user => (
      <WorkerInputLabel user={user} employer={employer} interimAgencyId={interimAgency} />
    ),
    sortBy: null,
  })

  // Workers that are still selectable (not already in the fieldValue).
  const selectableWorkers = allWorkers?.filter(w => !fieldValue.some(dWId => dWId === w._id))
  const selectableItems = buildListAsInputOptions(selectableWorkers ?? [], {
    labelBuilder: user => getUserLabel(user),
    renderLabel: user => (
      <WorkerInputLabel user={user} employer={employer} interimAgencyId={interimAgency} />
    ),
  })

  if (!interimAgency)
    return (
      <Message color="warning">
        <Message.Body>Vous n'avez pas encore sélectionné une Agence d'Intérim.</Message.Body>
      </Message>
    )

  if (workersQuery.isFetched && !workersQuery.data?.[0])
    return (
      <Message color="warning">
        <Message.Body>
          <b>
            Aucun <WorkerLabel /> à suggérer
          </b>
          <br />
          Vous n'avez pas encore collaboré avec des <WorkerLabel plural /> via l'Agence d'Intérim
          sélectionnée
        </Message.Body>
      </Message>
    )

  return (
    <>
      <Field
        component={ListField}
        label="Intérimaire(s) suggéré(s) pour ce poste"
        required
        name={fieldName}
        items={selectedItems}
        fullwidth="true"
        disabled={workersRequest && !workersRequest.__actions.canBeUpdated}
      />
      <ControlledAutocomplete
        items={selectableItems as AutocompleteFieldItem[]}
        onlySelect
        clearOnSelect
        showItemsAfterSelect
        placeholder="Choisir..."
        value=""
        onSelect={(value, item) => {
          // Only react to "real" selections (not free-text typing).
          if (value && item) {
            setFieldValue(fieldName, [...fieldValue, value])
          }
        }}
      />
    </>
  )
}

export default DesiredWorkersField
